/**
 * Proyecto: MOGOTAX-CONDUCTOR
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 05 de Septiembre 2026
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS
 *
 * Copyright (c) 2026 Megatrack GPS SAS. Todos los derechos reservados.
 */

import React, { useState, useEffect } from "react";
import { Modal, Input, Button, Alert, Checkbox, message } from "antd";
import { CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

const { TextArea } = Input;

/**
 * Modal para marcar traslado como Finalizado por Validar
 * Se usa cuando el conductor no puede recolectar firma ni OTP del paciente
 * Requiere confirmación bajo juramento del conductor
 */
const FinalizadoValidacionModal = ({ visible, onClose, onConfirm }) => {
  const [motivo, setMotivo] = useState("");
  const [juramentoAceptado, setJuramentoAceptado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  // Limpiar cuando se cierra el modal
  useEffect(() => {
    if (!visible) {
      setMotivo("");
      setJuramentoAceptado(false);
    }
  }, [visible]);

  const handleConfirmar = async () => {
    if (!motivo.trim()) {
      message.error("Por favor, indique el motivo");
      return;
    }

    if (!juramentoAceptado) {
      message.error("Debe confirmar bajo juramento para continuar");
      return;
    }

    setConfirmando(true);
    try {
      await onConfirm({ motivo });
    } finally {
      setConfirmando(false);
    }
  };

  const puedeConfirmar = juramentoAceptado && motivo.trim().length > 0;

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircleOutlined style={{ color: "#722ED1", fontSize: 24 }} />
          <span>Finalizar sin Firma</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancelar" onClick={onClose} disabled={confirmando}>
          Cancelar
        </Button>,
        <Button
          key="confirmar"
          type="primary"
          onClick={handleConfirmar}
          disabled={!puedeConfirmar}
          loading={confirmando}
          style={{ backgroundColor: "#722ED1", borderColor: "#722ED1" }}
        >
          Confirmar
        </Button>,
      ]}
      closable={!confirmando}
      maskClosable={false}
      width={500}
    >
      <div style={{ padding: "20px 0" }}>
        {/* Información */}
        <Alert
          message="Finalizar sin firma del paciente"
          description="Esta opción se usa cuando no es posible recolectar la firma del paciente ni verificar con código OTP. El traslado será validado posteriormente en Centro de Control con código OTP."
          type="info"
          icon={<ExclamationCircleOutlined />}
          showIcon
          style={{ marginBottom: 20, backgroundColor: "#F9F0FF", borderColor: "#D3ADF7" }}
        />

        {/* Motivo */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>
            Motivo <span style={{ color: "red" }}>*</span>
          </label>
          <TextArea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: Paciente no puede firmar, no tiene teléfono disponible, paciente inconsciente..."
            rows={4}
            maxLength={500}
            showCount
            disabled={confirmando}
          />
        </div>

        {/* Juramento */}
        <div
          style={{
            backgroundColor: "#F9F0FF",
            padding: 16,
            borderRadius: 8,
            border: "1px solid #D3ADF7",
          }}
        >
          <Checkbox
            checked={juramentoAceptado}
            onChange={(e) => setJuramentoAceptado(e.target.checked)}
            disabled={confirmando}
            style={{ lineHeight: 1.6 }}
          >
            <span style={{ fontWeight: 500 }}>
              Confirmo bajo juramento que realicé el servicio de traslado y no fue posible
              obtener la firma ni el código OTP del paciente.
            </span>
          </Checkbox>
        </div>

        {/* Nota adicional */}
        <div style={{ marginTop: 16, fontSize: 12, color: "#8c8c8c", fontStyle: "italic" }}>
          Este traslado quedará pendiente de validación con código OTP en Centro de Control.
          No se generará el Control de Servicio hasta que sea validado.
        </div>
      </div>
    </Modal>
  );
};

export default FinalizadoValidacionModal;
